import React, { PropsWithChildren, useContext } from 'react'
import { AppBar, Toolbar, IconButton, Box, ListItemIcon, Menu, MenuItem } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useLocation, useNavigate } from 'react-router-dom'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import authContext from '../../context'
import TextLabel from '../TextLabel'
import { useTranslation } from 'react-i18next'
import { Settings, Logout, PersonAdd } from '@mui/icons-material'
import AdminSettingPage from '../AdminSettingPage'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import StyledTooltip from '../../styledComponents/styledTooltip'
import { logUserAction } from '../../utils/apiUtils'
import { useGetToken } from '../../hooks/useGetToken'
import { CONSTANTS } from '../../CONSTANTS'


const useStyles = makeStyles()((theme) => ({
  root: {
    top: '26px',
    left: '0.5px',
    // width: '1280px',
    height: '59px',
    /* UI Properties */
    opacity: 1,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    top: '26px',
    left: '105px',
    width: '251px',
    textAlign: 'left',
    letterSpacing: '0px',
    color: '#3D3D3D',
    opacity: 1,
    marginTop: '8px'
  },
}))

interface HeaderProps {
  adminRole?: string;
  onSignOut: () => void; // Function to handle sign out
}


const Header: React.FC<HeaderProps> = ({ adminRole, onSignOut }): JSX.Element => {

  const {
    authState: { user }
  } = useContext(authContext)
  const [getToken] = useGetToken();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const location = useLocation()
  const { pathname } = location

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Toggle menu open/close using anchor element
  const handleToggleMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleCloseMenu();
    const token = await getToken();
    await logUserAction(token, user?.username || '', CONSTANTS.USER_ACTION.LOGOUT)
    onSignOut();

  };
  const handleSettingClick = () => {
    setIsSettingsOpen(true);
    handleCloseMenu();
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  const { classes } = useStyles()
  return (
    <div className={classes.root}>
      <AppBar position="static" color="transparent">
        <Toolbar>
          <img src="/icon_kone_logo.jpg" alt="loading" style={{ marginRight: '20px' }} />
          <TextLabel type='title' variant="h6" className={classes.title}>
            {t('smp')}
            <TextLabel type='smallText'
              variant="subtitle1"
              style={{
                top: '50px',
                left: '105px',
                width: '152px',
                height: '17px',
                letterSpacing: '0px',
                textAlign: 'left',
                color: '#3D3D3D',
                opacity: 1,
              }}
            >
              {String(t('adminDashboard.title'))}
              {/* {adminRole === SUPER_ADMIN_USER ? String(t('adminDashboard.roles.superAdmin')) : String(t('adminDashboard.roles.supervisor'))} */}
            </TextLabel>
          </TextLabel>

          <StyledTooltip adminRole={adminRole || ''}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                position: 'absolute',
                right: '20px',
              }}
            >
              <IconButton color="inherit">
                <img src='/icon_user.jpg' style={{
                  fontFamily: 'var(--unnamed-font-family-inter)',
                  fontStyle: 'var(--unnamed-font-style-normal)',
                  fontWeight: 'var(--unnamed-font-weight-normal)',
                  fontSize: 'var(--unnamed-font-size-14)',
                  lineHeight: 'var(--unnamed-line-spacing-32)',
                  letterSpacing: 'var(--unnamed-character-spacing-0)',
                  color: 'var(--unnamed-color-1450f5)'
                }} />
              </IconButton>
              <TextLabel type='regularText'
                style={{
                  letterSpacing: '0px',
                  textAlign: 'left',
                  color: '#3D3D3D',
                  marginRight: '10px',
                }}
              >
                {user?.username}
              </TextLabel>

              <IconButton
                aria-label="expand row"
                size="small"
                onClick={handleToggleMenu}
                aria-controls={open ? 'user-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </div>
          </StyledTooltip>

          {/* Menu anchored to the arrow button */}
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleCloseMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            // Remove margin top to avoid gap below button
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                // mt: 1.5, // Removed this margin top
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
          >
            <MenuItem onClick={
              handleSettingClick
              // your setting click handler here
            }>
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              {t('settings.title')}
            </MenuItem>

            <MenuItem onClick={handleCloseMenu}>
              <ListItemIcon>
                <HelpOutlineOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {t('settings.help')}
            </MenuItem>

            <MenuItem onClick={handleSignOut}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              {t('settings.signOut')}
            </MenuItem>
          </Menu>

          {isSettingsOpen && <AdminSettingPage isOpen={isSettingsOpen} onClose={handleCloseSettings} />}
        </Toolbar>
      </AppBar>
    </div>
  )
}
export default Header
